// A station class's own PC settings, in its Edit dialog (spec/13 §12, §13; 2026-10-07): the settings that take a
// station class's scope — how a PC clears between players, which programs and USB drives a player may use, Alt+F4 and
// a restricted Explorer — each following the venue's setting unless it is changed for this class. Per-class values
// were invisible before ("N station classes or zones have values of their own"); here they are read and changed.
//
// The same resource and rules as the Settings screen — `GET/PUT …/venues/{v}/settings` with its ETag, each change
// naming the class (`stationClassId`), a reset removing the class's own value — through the same renderer, so a 412,
// a lock above the venue and the member's permission (`editableScopes`) read as they do there. Nothing new on the wire.

import type { ApiClient, SettingEntry, SettingsView } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Skeleton } from '@clavian/ui-web';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, type ReactNode } from 'react';

import { useApi } from '../api/client';
import { versioned, type Versioned } from '../api/versioned';
import { LoadFailure } from '../shell/LoadFailure';
import { useRegistryLabels, useSettingHelp } from '../settings/labels';
import { settingMoney } from '../settings/money';
import { readSettings, settingsKey, toRenderable, type SettingsScope } from '../settings/registry-source';
import { SettingsForm } from '../settings/SettingsForm';
import { useSettingsView } from '../settings/SettingsPage';
import type { RenderableSetting, SettingChange, SettingsPart, SourceWords } from '../settings/types';

/** The settings a class can keep a value of its own for: every one the venue's screen lists with class scope. */
export const classScoped = (entry: SettingEntry): boolean => entry.scopes.includes('stationClass');

/** One class's view of a class-scoped setting: its own value if it has one, the venue's otherwise. */
export function classEntry(entry: RenderableSetting, raw: SettingEntry, classId: string): RenderableSetting {
  const own = raw.overrides.find((place) => place.scopeType === 'stationClass' && place.stationClassId === classId);
  const editable = raw.editableScopes.includes('stationClass') && raw.lockedAt === undefined;
  // A class's rows stand alone under one heading: no other classes, no layout section, no Advanced or nesting.
  const {
    classValues: _others,
    placeOverrides: _count,
    why: _why,
    venueValues: _venues,
    readOnlyReason: _reason,
    sectionId: _section,
    advanced: _advanced,
    dependsOn: _dependsOn,
    section: _heading,
    ...rest
  } = entry;
  // One card under the tab's own name: no sub-heading of its own.
  return {
    ...rest,
    value: own === undefined ? raw.value : own.value,
    // "Using this venue's setting": the venue's value is what a reset gives, and where it comes from.
    source: own === undefined ? raw.source : 'venue',
    hasOverride: own !== undefined,
    inherited: { value: raw.value, source: raw.source },
    editable,
  };
}

function writeClassSettings(api: ApiClient, scope: Extract<SettingsScope, { kind: 'venue' }>, classId: string, changes: readonly SettingChange[], etag: string) {
  return versioned((onResponse) =>
    api.putVenueSettings({
      path: { organizationId: scope.organizationId, venueId: scope.venueId },
      headerParams: { 'if-match': etag },
      body: {
        changes: changes.map((change) =>
          change.reset ? { key: change.key, reset: true, stationClassId: classId } : { key: change.key, value: change.value, stationClassId: classId },
        ),
      },
      onResponse,
    }),
  );
}

export function ClassSettings({ organizationId, venueId, classId }: { readonly organizationId: string; readonly venueId: string; readonly classId: string }): ReactNode {
  const { t } = useTranslation('console');
  const api = useApi();
  const queryClient = useQueryClient();
  const labels = useRegistryLabels();
  const helpOf = useSettingHelp();
  const formatter = useVenueFormatter();
  const scope = useMemo(() => ({ kind: 'venue' as const, organizationId, venueId }), [organizationId, venueId]);
  const { query } = useSettingsView(scope);
  const key = settingsKey(scope);

  const part = useMemo<SettingsPart | undefined>(() => {
    const view = query.data;
    if (view === undefined) return undefined;
    const currency = view.data.scope.currency;
    const words = { label: (settingKey: string) => labels.field(settingKey, []), help: helpOf };
    const entriesOf = (next: Versioned<SettingsView>) => ({
      data: next.data.settings
        .filter(classScoped)
        .map((raw) => classEntry(toRenderable(raw, words, { screen: 'venue', ...(currency ? { money: settingMoney(formatter, currency) } : {}) }), raw, classId))
        // In the order the Settings screen shows them.
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
      etag: next.etag,
    });
    const first = entriesOf(view);
    return {
      id: 'class',
      entries: first.data,
      etag: first.etag,
      save: async (changes, etag) => {
        const next = await writeClassSettings(api, scope, classId, changes, etag);
        queryClient.setQueryData(key, next);
        return entriesOf(next);
      },
      reload: async () => {
        const next = await readSettings(api, scope);
        queryClient.setQueryData(key, next);
        return entriesOf(next);
      },
      fieldKey: (field, changes) => {
        const match = /^changes\.(\d+)/.exec(field);
        return match === null ? undefined : changes[Number(match[1])]?.key;
      },
    };
  }, [query.data, labels, helpOf, formatter, classId, api, scope, queryClient, key]);

  const sources = useMemo<SourceWords>(
    () => ({
      badge: (source) => t(`settings.source.${source}`),
      name: (source) => t(`settings.sourceName.${source}`),
      using: (_source, value) => t('settings.classSettings.usingVenue', { value }),
      changed: t('settings.classSettings.changed'),
      resetTo: (_source, value) => t('settings.classSettings.resetToVenue', { value }),
    }),
    [t],
  );

  if (part === undefined) {
    return query.isError ? <LoadFailure error={query.error} message={t('settings.classSettings.unavailable')} onRetry={() => void query.refetch()} /> : <Skeleton shape="text" lines={4} />;
  }
  if (part.entries.length === 0) return null;
  const editable = part.entries.some((entry) => entry.editable);
  return (
    <div className="console-class-settings">
      <p className="console-subtle">{t('settings.classSettings.intro')}</p>
      <SettingsForm key={`${key.join('/')}:class:${classId}`} parts={[part]} labels={labels} sources={sources} notice={editable ? undefined : t('settings.classSettings.readOnly')} />
    </div>
  );
}
